import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import axios from 'axios';
import { FaUserClock, FaUserPlus } from 'react-icons/fa';

import TeamsList from './components/TeamsList';
import TeamFormModal from './components/TeamFormModal';
import MemberCard from './components/MemberCard';
import MemberDetailsModal from './components/MemberDetailsModal';
import InterviewSetupModal from '../../components/Teams/interview/InterviewSetupModal';
import AddMemberOptionsModal from './components/AddMemberOptionsModal';
import InterviewVideoCall from '../../components/Teams/interview/InterviewVideoCall';
import MilitarySessionVideoCall from '../../components/Teams/assessment/MilitarySessionVideoCall';
import MilitaryBatchManager from '../../components/Teams/assessment/MilitaryBatchManager';
import DetailedAnalyticsModal from '../../components/Teams/interview/DetailedAnalyticsModal';
import EndInterviewModal from '../../components/Teams/interview/EndInterviewModal';
import PendingCandidatesModal from '../../components/Teams/interview/PendingCandidatesModal';

import { getTeamTypeDisplay } from '../../utils/teamTypeUtils';
import { useInterviewHandlers } from './hooks/useInterviewHandlers';
import { useMilitarySessionHandlers } from './hooks/useMilitarySessionHandlers';
import { useTeamOperations } from './hooks/useTeamOperations';
import useEndInterview from './hooks/useEndInterview';

import config from '../../config';
import './Teams.css';
import { toast } from '../../components/Common/Toast';

const Teams = () => {
  const { userInfo: user, token } = useSelector(state => state.user);
  const currentUserId = user?.id || user?.user_id;

  // ─── Core State ───
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');

  // ─── Interview State ───
  const [isCheckingInterview, setIsCheckingInterview] = useState(false);
  const [hasCheckedInitialInterview, setHasCheckedInitialInterview] = useState(false);
  const [isStartingInterview, setIsStartingInterview] = useState(false);
  const [activeInterview, setActiveInterview] = useState(null);
  const [interviewQuestions, setInterviewQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [isInterviewActive, setIsInterviewActive] = useState(false);
  const [interviewStartTime, setInterviewStartTime] = useState(null);
  const [showCandidateQuestions, setShowCandidateQuestions] = useState(false);
  const [currentInterviewForCandidate, setCurrentInterviewForCandidate] = useState(null);
  const [pendingInterview, setPendingInterview] = useState(null);
  const [showInterviewConfirm, setShowInterviewConfirm] = useState(false);
  const [waitingForNext, setWaitingForNext] = useState(false);
  const [nextQuestionTime, setNextQuestionTime] = useState(null);
  const [interviewCompleted, setInterviewCompleted] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [candidateProgress, setCandidateProgress] = useState(null);
  const [askedQuestionIds, setAskedQuestionIds] = useState([]);
  const [currentRound, setCurrentRound] = useState(null);
  const [completedRounds, setCompletedRounds] = useState([]);
  const [totalRounds] = useState(5);

  const [showGroupVideoCall, setShowGroupVideoCall] = useState(false);
  const [groupInterviewId, setGroupInterviewId] = useState(null);
  const [groupQuestions, setGroupQuestions] = useState([]);
  const [groupCandidates, setGroupCandidates] = useState([]);
  const [isGroupCallMinimized, setIsGroupCallMinimized] = useState(false);

  const [videoCallSelectedUser, setVideoCallSelectedUser] = useState(null);

  // ─── Military State ───
  const [showMilitaryVideoCall, setShowMilitaryVideoCall] = useState(false);
  const [militarySessionId, setMilitarySessionId] = useState(null);
  const [activeMilitarySession, setActiveMilitarySession] = useState(null);
  const [isMilitaryCallMinimized, setIsMilitaryCallMinimized] = useState(false);
  const [pendingMilitarySession, setPendingMilitarySession] = useState(null);
  const [showMilitarySessionConfirm, setShowMilitarySessionConfirm] = useState(false);
  const [hasCheckedInitialMilitarySession, setHasCheckedInitialMilitarySession] = useState(false);
  const [showBatchManager, setShowBatchManager] = useState(false);
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [currentBatchData, setCurrentBatchData] = useState(null);
  const [activeBatchSessions, setActiveBatchSessions] = useState([]);

  // ─── Modal State ───
  const [showTeamFormModal, setShowTeamFormModal] = useState(false);
  const [showAddMemberOptions, setShowAddMemberOptions] = useState(false);
  const [showMemberDetails, setShowMemberDetails] = useState(null);
  const [teamToEdit, setTeamToEdit] = useState(null);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [showAdminQuestions, setShowAdminQuestions] = useState(false);
  const [selectedInterviewId, setSelectedInterviewId] = useState(null);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsInterviewId, setAnalyticsInterviewId] = useState(null);
  const [analyticsCandidateId, setAnalyticsCandidateId] = useState(null);
  const [showPendingCandidates, setShowPendingCandidates] = useState(false);
  const [pendingCandidates, setPendingCandidates] = useState([]);
  const [loadingPendingCandidates, setLoadingPendingCandidates] = useState(false);
  const [pendingCandidatesCount, setPendingCandidatesCount] = useState(0);

  // ─── Refs ───
  const timers = useRef({});
  const askedQuestionsRef = useRef([]);
  const mountedRef = useRef(true);
  const activeInterviewIdRef = useRef(null);
  const isInterviewEndedByAdminRef = useRef(false);
  const batchCheckDoneRef = useRef(false);

  let fetchTeamMembers = null;

  const clearInterviewTimers = () => {
    if (timers.current.first) clearTimeout(timers.current.first);
    if (timers.current.interval) clearInterval(timers.current.interval);
    if (timers.current.candidate) clearTimeout(timers.current.candidate);
    if (timers.current.candidateInterval) clearInterval(timers.current.candidateInterval);
  };

  const handleStartMilitaryProgram = (programData) => {
    console.log('Military program started:', programData);
    if (programData.candidates && programData.candidates.length > 0) {
      setCurrentBatchId(programData.batchId);
      setCurrentBatchData(programData);
      setShowBatchManager(true);
      toast.success(`Started assessment for ${programData.candidates.length} candidate(s)`);
      setShowAddMemberOptions(false);
      checkActiveBatchSessions();
    } else if (programData.session) {
      setMilitarySessionId(programData.session.session_id);
      setActiveMilitarySession(programData.session);
      setVideoCallSelectedUser(programData.selectedFriend || {
        first_name: programData.candidate_name || 'Candidate',
        last_name: '',
        user_id: programData.user_id,
      });
      setShowMilitaryVideoCall(true);
      setIsMilitaryCallMinimized(false);
      setShowAddMemberOptions(false);
    }
  };

  const {
    loadAvailableQuestions,
    checkActiveInterview,
    checkIfUserInActiveInterview,
    checkPendingInterviewQuestions,
    logCandidateQuestion,
    askCandidateQuestion,
    handleStartInterview,
    handleEndInterview: endInterviewPerCandidate,
    getNextQuestion,
  } = useInterviewHandlers(
    token, user, selectedTeam, activeInterview, setActiveInterview, setIsInterviewActive,
    setInterviewQuestions, setCurrentQuestion, setCurrentQuestionIndex,
    setInterviewStartTime, setCandidateProgress, setCurrentInterviewForCandidate,
    setShowCandidateQuestions, setWaitingForNext, setNextQuestionTime, setInterviewCompleted,
    setPendingInterview, setShowInterviewConfirm, setShowAddMemberOptions, setShowInterviewModal,
    activeInterviewIdRef, isInterviewEndedByAdminRef, askedQuestionsRef,
    timers, availableQuestions, setAvailableQuestions, isAdmin,
    isCheckingInterview, setIsCheckingInterview, showCandidateQuestions, showInterviewConfirm,
    waitingForNext, interviewCompleted, isInterviewActive, interviewQuestions, currentQuestionIndex,
    currentQuestion, currentInterviewForCandidate, fetchTeamMembers, setIsStartingInterview,
    setLoading, isStartingInterview, setAskedQuestionIds, mountedRef, setCurrentRound,
    totalRounds, setCompletedRounds, completedRounds, setAnsweredQuestions, setTeams, setSelectedTeam
  );

  const {
    checkUserActiveMilitarySession,
    handleStartMilitarySession,
    handleStartMilitaryFromAddModal,
    handleJoinMilitarySession,
    handleMilitarySessionComplete,
    handleMilitarySessionClose,
    abandonSession,
  } = useMilitarySessionHandlers(
    token, user, selectedTeam,
    setShowMilitaryVideoCall, setMilitarySessionId, setActiveMilitarySession,
    setIsMilitaryCallMinimized, setVideoCallSelectedUser,
    setPendingMilitarySession, setShowMilitarySessionConfirm,
    teamMembers, fetchTeamMembers, handleStartMilitaryProgram
  );

  const {
    fetchTeams, fetchFriends,
    fetchTeamMembers: fetchTeamMembersOp,
    handleCreateTeam, handleUpdateTeam, handleDeleteTeam,
    handleAddMember, handleRemoveMember, handleExitTeam,
  } = useTeamOperations(
    token, user, teams, setTeams, selectedTeam, setSelectedTeam,
    teamMembers, setTeamMembers, setShowTeamFormModal, teamToEdit, setTeamToEdit,
    setShowAddMemberOptions, setLoading, setFriends
  );

  fetchTeamMembers = fetchTeamMembersOp;

  const {
    handleEndInterview,
    showEndInterviewModal,
    setShowEndInterviewModal,
    handleCandidateAction,
    handleMeetingEndedCleanup,
    endInterviewId,
    isEnding
  } = useEndInterview(
    token, selectedTeam, fetchTeamMembers, setActiveInterview, setIsInterviewActive,
    setTeams, setSelectedTeam, setShowGroupVideoCall, setIsGroupCallMinimized,
    activeInterviewIdRef, clearInterviewTimers
  );

  const handleStartGroupInterview = async (interviewConfig) => {
    try {
      const result = await handleStartInterview(interviewConfig);
      if (result?.interview) {
        setGroupInterviewId(result.interview.interview_id);
        setGroupQuestions(result.interview.questions || []);
        setGroupCandidates(result.interview.candidates || []);
        setShowGroupVideoCall(true);
        setIsGroupCallMinimized(false);
      }
    } catch (err) {
      console.error('Failed to start group interview:', err);
      toast.error('Failed to start interview');
    }
  };

  const handleAcceptInterview = (interview) => {
    activeInterviewIdRef.current = interview.interview_id;
    setShowInterviewConfirm(false);
    setCurrentInterviewForCandidate({
      interviewId: interview.interview_id,
      teamName: interview.team_name,
      role: interview.role || 'candidate',
      candidateId: interview.candidateId,
      questions: interview.questions || [],
    });
    setShowCandidateQuestions(false);
    setWaitingForNext(false);
    setInterviewCompleted(false);
    setPendingInterview(null);
    isInterviewEndedByAdminRef.current = false;
    setGroupInterviewId(interview.interview_id);
    setGroupQuestions(interview.questions || []);
    setGroupCandidates(interview.candidates || []);
    setShowGroupVideoCall(true);
    setIsGroupCallMinimized(false);
  };

  const handleSelectTeam = (team) => {
    setActiveInterview(null);
    setIsInterviewActive(false);
    setInterviewQuestions([]);
    setCurrentQuestionIndex(0);
    setCurrentQuestion(null);
    setInterviewStartTime(null);
    setCandidateProgress(null);
    setShowMilitaryVideoCall(false);
    setMilitarySessionId(null);
    setActiveMilitarySession(null);
    setShowBatchManager(false);
    setCurrentBatchId(null);
    setCurrentBatchData(null);
    setShowGroupVideoCall(false);
    setGroupInterviewId(null);
    setGroupCandidates([]);
    setGroupQuestions([]);
    setVideoCallSelectedUser(null);
    isInterviewEndedByAdminRef.current = false;
    activeInterviewIdRef.current = null;
    batchCheckDoneRef.current = false;
    clearInterviewTimers();
    setSelectedTeam(team);
    setIsAdmin(String(team.created_by) === String(currentUserId));
    fetchTeamMembersOp(team.team_id);
  };

  const handleJoinMilitarySessionWrapper = () => {
    handleJoinMilitarySession(pendingMilitarySession);
  };

  const handleBatchManagerClose = () => {
    setShowBatchManager(false);
    setCurrentBatchId(null);
    setCurrentBatchData(null);
    if (selectedTeam?.team_id) fetchTeamMembersOp(selectedTeam.team_id);
    checkActiveBatchSessions();
  };

  const handleBatchComplete = () => {
    if (selectedTeam?.team_id) fetchTeamMembersOp(selectedTeam.team_id);
    toast.success('Batch assessment session completed');
    checkActiveBatchSessions();
  };

  const checkActiveBatchSessions = useCallback(async () => {
    if (!selectedTeam?.team_id || !currentUserId || batchCheckDoneRef.current) return;
    batchCheckDoneRef.current = true;
    try {
      const response = await axios.get(
        `${config.baseURL}/api/military/teams/${selectedTeam.team_id}/active-batches`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success && response.data.batches) {
        setActiveBatchSessions(response.data.batches);
      }
    } catch (error) {
      console.error('Error checking active batches:', error);
    }
  }, [selectedTeam?.team_id, token, currentUserId]);

  const handleOpenBatchManager = (batchId) => {
    setCurrentBatchId(batchId);
    setShowBatchManager(true);
  };

  const fetchPendingCandidatesCount = useCallback(async () => {
    if (!selectedTeam?.team_id || !isAdmin) return;
    try {
      const { data } = await axios.get(
        `${config.baseURL}/api/teams/${selectedTeam.team_id}/pending-candidates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) setPendingCandidatesCount(data.count || 0);
    } catch (error) {
      console.error('Error fetching pending candidates count:', error);
    }
  }, [selectedTeam?.team_id, token, isAdmin]);

  const fetchPendingCandidates = useCallback(async () => {
    if (!selectedTeam?.team_id) return;
    setLoadingPendingCandidates(true);
    try {
      const { data } = await axios.get(
        `${config.baseURL}/api/teams/${selectedTeam.team_id}/pending-candidates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) {
        setPendingCandidates(data.candidates || []);
        setPendingCandidatesCount(data.count || 0);
      }
    } catch (error) {
      toast.error('Failed to fetch pending candidates');
    } finally {
      setLoadingPendingCandidates(false);
    }
  }, [selectedTeam?.team_id, token]);

  const handleStartInterviewWithPending = useCallback((candidate) => {
    setShowPendingCandidates(false);
    handleStartGroupInterview({
      mode: candidate.interview_mode || 'group',
      capture: [],
      candidates: [candidate.candidate_id]
    });
  }, [handleStartGroupInterview]);

  // ─── Effects ───
  useEffect(() => {
    const handler = ({ detail }) => {
      setAnalyticsInterviewId(detail.interviewId);
      setAnalyticsCandidateId(detail.candidateId || null);
      setShowAnalyticsModal(true);
    };
    window.addEventListener('openAnalyticsModal', handler);
    return () => window.removeEventListener('openAnalyticsModal', handler);
  }, []);

  useEffect(() => {
    if (!token || !currentUserId) return;
    const socket = io(config.baseURL, { auth: { token } });

    socket.on("connect_error", (err) => { console.log("Socket error:", err.message); });

    socket.on('interview-invitation', (interview) => {
      if (activeInterviewIdRef.current || isInterviewActive || showGroupVideoCall) return;
      if (!interview?.interview_id) return;
      setPendingInterview(interview);
      setShowInterviewConfirm(true);
    });

    socket.on('interview-ended', (data) => {
      const { interviewId, decision } = data;
      const isCurrent = String(groupInterviewId) === String(interviewId) ||
        String(activeInterviewIdRef.current) === String(interviewId);
      if (!isCurrent) return;
      setShowGroupVideoCall(false);
      setIsGroupCallMinimized(false);
      setGroupInterviewId(null);
      setGroupQuestions([]);
      setGroupCandidates([]);
      setActiveInterview(null);
      setIsInterviewActive(false);
      setCurrentInterviewForCandidate(null);
      setShowCandidateQuestions(false);
      setWaitingForNext(false);
      setInterviewCompleted(false);
      setPendingInterview(null);
      setShowInterviewConfirm(false);
      activeInterviewIdRef.current = null;
      isInterviewEndedByAdminRef.current = false;
      clearInterviewTimers();
      if (selectedTeam?.team_id && fetchTeamMembers) fetchTeamMembers(selectedTeam.team_id);
      fetchTeams();
      if (decision === 'selected') {
        toast.success('Interview completed - You have been selected!');
      } else {
        toast.info('Interview has ended');
      }
    });

    socket.on('candidate-interview-ended', ({ teamMemberAdded }) => {
      if (teamMemberAdded) {
        toast.success('Candidate has been added to the team!');
        if (selectedTeam?.team_id) fetchTeamMembers(selectedTeam.team_id);
      }
    });

    return () => {
      socket.off('connect');
      socket.off('interview-invitation');
      socket.off('interview-ended');
      socket.off('candidate-interview-ended');
      socket.disconnect();
    };
  }, [token]);

  useEffect(() => {
    if (currentUserId) {
      fetchTeams();
      fetchFriends();
      loadAvailableQuestions();
    }
  }, [currentUserId]);

  useEffect(() => {
    const checkActiveInterviewStatus = async () => {
      if (!selectedTeam?.team_id) return;
      try {
        const { data } = await axios.get(
          `${config.baseURL}/api/teams/${selectedTeam.team_id}/active-interview`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (data?.success) {
          if (data.active_interview) {
            setActiveInterview(data.active_interview);
            setIsInterviewActive(true);
            activeInterviewIdRef.current = data.active_interview.interview_id;
            setSelectedTeam(prev => prev ? { ...prev, active_interview: data.active_interview } : prev);
          } else {
            setActiveInterview(null);
            setIsInterviewActive(false);
            activeInterviewIdRef.current = null;
            setSelectedTeam(prev => prev ? { ...prev, active_interview: null } : prev);
          }
        }
      } catch (err) {
        console.error('Error checking active interview:', err);
      }
    };
    if (selectedTeam && token) checkActiveInterviewStatus();
  }, [selectedTeam?.team_id, token]);

  useEffect(() => {
    if (selectedTeam) setIsAdmin(String(selectedTeam.created_by) === String(currentUserId));
  }, [selectedTeam, currentUserId]);

  useEffect(() => {
    if (!currentUserId || !token || hasCheckedInitialInterview) return;
    const runInitialChecks = async () => {
      setHasCheckedInitialInterview(true);
      let hasRegularInterview = false;
      try {
        const { data } = await axios.get(
          `${config.baseURL}/api/teams/user/active-interview`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (data?.success && data?.active_interview && !showInterviewConfirm && !interviewCompleted && !currentInterviewForCandidate) {
          hasRegularInterview = true;
          setPendingInterview(data.active_interview);
          setShowInterviewConfirm(true);
          setIsAdmin(String(data?.active_interview?.admin_id) === String(currentUserId));
        }
      } catch (err) {
        console.error('Error checking active interview on mount:', err);
      }
      await checkUserActiveMilitarySession({ skipIfInterviewPending: hasRegularInterview });
    };
    runInitialChecks();
  }, [currentUserId, token]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; clearInterviewTimers(); };
  }, []);

  useEffect(() => {
    const handler = async (event) => {
      if (selectedTeam?.team_id === event.detail.teamId && fetchTeamMembersOp) {
        await fetchTeamMembersOp(event.detail.teamId);
      }
    };
    window.addEventListener('refreshTeamMembers', handler);
    return () => window.removeEventListener('refreshTeamMembers', handler);
  }, [selectedTeam?.team_id, fetchTeamMembersOp]);

  useEffect(() => {
    if (selectedTeam) {
      batchCheckDoneRef.current = false;
      checkActiveBatchSessions();
      fetchPendingCandidatesCount();
    }
  }, [selectedTeam, checkActiveBatchSessions, fetchPendingCandidatesCount]);

  // ─── Computed Values ───
  const availableFriends = friends.filter(
    f => !teamMembers.some(m => String(m.user_id) === String(f.user_id))
  );

  const filteredTeamMembers = teamMembers.filter(member => {
    if (!memberSearchQuery.trim()) return true;
    const q = memberSearchQuery.toLowerCase().trim();
    const firstName = (member.first_name || '').toLowerCase();
    const lastName = (member.last_name || '').toLowerCase();
    const fullName = `${firstName} ${lastName}`.trim();
    return ['email', 'username', 'profileId'].some(field => {
      return (member[field] || '').toLowerCase().includes(q);
    }) || firstName.includes(q) || lastName.includes(q) || fullName.includes(q);
  });

  if (!user) {
    return (
      <div className="teams-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="teams-page">
      <div className="teams-header">
        <h1 className="teams-title">Teams Management</h1>
        <p className="teams-subtitle">Create teams and manage members from your friends list</p>
      </div>

      <div className="teams-layout">
        <div className="teams-left-panel">
          <TeamsList
            teams={teams}
            selectedTeam={selectedTeam}
            onSelectTeam={handleSelectTeam}
            onEditTeam={(team) => { setTeamToEdit(team); setFormMode('edit'); setShowTeamFormModal(true); }}
            onDeleteTeam={handleDeleteTeam}
            onCreateTeam={() => { setTeamToEdit(null); setFormMode('create'); setShowTeamFormModal(true); }}
            currentUserId={currentUserId}
            onExitTeam={handleExitTeam}
          />
        </div>

        <div className="teams-right-panel">
          {selectedTeam ? (
            <>
              <div className="team-details-header">
                <div className="team-title-section">
                  <div className="team-title-row">
                    <h2 className="team-details-title">{selectedTeam.team_name}</h2>
                    {getTeamTypeDisplay(selectedTeam) && (
                      <span className={`team-type-simple ${getTeamTypeDisplay(selectedTeam).className}`}>
                        {getTeamTypeDisplay(selectedTeam).fullDisplay}
                      </span>
                    )}
                  </div>
                  {selectedTeam.description && (
                    <p className="team-details-description">{selectedTeam.description}</p>
                  )}
                </div>

                <div className="team-actions-nav">
                  {isAdmin && (
                    <>
                      <button className="add-member-btn" onClick={() => setShowAddMemberOptions(true)} disabled={isInterviewActive}>
                        <FaUserPlus /> Add Members
                      </button>

                      {pendingCandidatesCount > 0 && (
                        <button className="pending-candidates-btn" onClick={() => { fetchPendingCandidates(); setShowPendingCandidates(true); }}>
                          <FaUserClock /> Pending ({pendingCandidatesCount})
                        </button>
                      )}

                      {isInterviewActive && selectedTeam?.active_interview && (
                        <button className="manage-interview-btn" onClick={() => {
                          const interview = selectedTeam.active_interview;
                          setGroupInterviewId(interview.interview_id);
                          setActiveInterview(interview);
                          setGroupQuestions([]);
                          setGroupCandidates([]);
                          if (interview.interview_id) activeInterviewIdRef.current = interview.interview_id;
                          setShowGroupVideoCall(true);
                          setIsGroupCallMinimized(false);
                        }}>
                          Manage Interview
                        </button>
                      )}

                      {activeBatchSessions.length > 0 && (
                        <div className="active-batch-dropdown">
                          <button className="manage-batch-btn" onClick={() => {
                            if (activeBatchSessions.length === 1) {
                              handleOpenBatchManager(activeBatchSessions[0].batch_id);
                            } else {
                              setShowBatchManager(false);
                            }
                          }}>
                            Manage Assessments
                          </button>
                          {activeBatchSessions.length > 1 && (
                            <div className="batch-dropdown-content">
                              {activeBatchSessions.map(batch => (
                                <button key={batch.batch_id} className="batch-option" onClick={() => handleOpenBatchManager(batch.batch_id)}>
                                  <div className="batch-option-info">
                                    <span className="batch-id-display">
                                      Batch: {batch.batch_id.length > 10 ? `${batch.batch_id.slice(0, 8)}...` : batch.batch_id}
                                    </span>
                                    <span className="batch-candidate-count">{batch.candidate_count} candidate{batch.candidate_count !== 1 ? 's' : ''}</span>
                                  </div>
                                  <span className={`batch-status ${batch.status}`}>Week {batch.current_week}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="team-members-section">
                <div className="members-header">
                  <h3 className="members-title">Team Members ({teamMembers.length})</h3>
                  {teamMembers.length > 0 && (
                    <div className="member-search-container">
                      <input
                        type="text"
                        className="member-search-input"
                        placeholder="Name, ID and Email"
                        value={memberSearchQuery}
                        onChange={(e) => setMemberSearchQuery(e.target.value)}
                      />
                      {memberSearchQuery && (
                        <button className="member-search-clear" onClick={() => setMemberSearchQuery('')} type="button">✕</button>
                      )}
                    </div>
                  )}
                </div>

                {teamMembers.length === 0 ? (
                  <div className="empty-members">
                    <div className="empty-icon">👤</div>
                    <h3>No Members Yet</h3>
                    <p>Add members from your friends list</p>
                  </div>
                ) : filteredTeamMembers.length === 0 ? (
                  <div className="empty-members">
                    <div className="empty-icon">🔍</div>
                    <h3>No Members Found</h3>
                    <p>No members match "{memberSearchQuery}"</p>
                  </div>
                ) : (
                  <div className="members-grid">
                    {filteredTeamMembers.map(member => (
                      <MemberCard
                        key={member.team_member_id}
                        isAdmin={isAdmin}
                        user={user}
                        token={token}
                        member={member}
                        onRemove={handleRemoveMember}
                        onViewDetails={setShowMemberDetails}
                        currentUserId={currentUserId}
                        teamId={selectedTeam?.team_id}
                        onStartInterview={handleStartGroupInterview}
                        onStartMilitarySession={handleStartMilitarySession}
                        teamName={selectedTeam?.team_name}
                        teamType={selectedTeam?.team_type}
                        onEndInterview={handleEndInterview}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="no-team-selected">
              <div className="empty-state">
                <div className="empty-icon">🏢</div>
                <h3>Select a Team</h3>
                <p>Choose a team from the list to view members and details</p>
                <button className="create-team-btn-large" onClick={() => { setTeamToEdit(null); setFormMode('create'); setShowTeamFormModal(true); }}>
                  Create your team
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <TeamFormModal
        show={showTeamFormModal}
        onClose={() => { setShowTeamFormModal(false); setTeamToEdit(null); }}
        onSubmit={formMode === 'edit' ? handleUpdateTeam : handleCreateTeam}
        loading={loading}
        teamToEdit={teamToEdit}
        mode={formMode}
      />

      <InterviewSetupModal
        show={showInterviewModal}
        onClose={() => setShowInterviewModal(false)}
        onStart={handleStartGroupInterview}
        teamName={selectedTeam?.team_name}
        selectedFriends={[]}
      />

      <AddMemberOptionsModal
        show={showAddMemberOptions}
        onClose={() => setShowAddMemberOptions(false)}
        teamId={selectedTeam?.team_id}
        teamName={selectedTeam?.team_name}
        teamType={selectedTeam?.team_type}
        friends={availableFriends}
        teamMembers={teamMembers}
        onAddMember={handleAddMember}
        onStartInterview={handleStartGroupInterview}
        onStartMilitaryProgram={handleStartMilitaryFromAddModal}
        token={token}
        user={user}
      />

      {showMemberDetails && (
        <MemberDetailsModal
          member={showMemberDetails}
          onClose={() => setShowMemberDetails(null)}
          currentUserId={currentUserId}
        />
      )}

      {showInterviewConfirm && pendingInterview && !isAdmin && (
        <div className="interview-confirm-overlay">
          <div className="interview-confirm-modal">
            <h2>Interview Invitation</h2>
            <p>You have been invited for an interview with team <strong>{pendingInterview.team_name}</strong></p>
            <p>Total Questions: {pendingInterview.questions?.length || pendingInterview.total_questions || 25}</p>
            <p>Would you like to start now?</p>
            <div className="confirm-actions">
              <button className="decline-btn" onClick={() => { setShowInterviewConfirm(false); setPendingInterview(null); }}>Later</button>
              <button className="accept-btn" onClick={() => handleAcceptInterview(pendingInterview)}>Start Now</button>
            </div>
          </div>
        </div>
      )}

      {showMilitarySessionConfirm && pendingMilitarySession && (
        <div className="interview-confirm-overlay">
          <div className="interview-confirm-modal">
            <h2>Military Assessment</h2>
            <p>You have an active military assessment with team <strong>{pendingMilitarySession.team_name}</strong></p>
            <div className="military-session-details">
              <div className="detail-row"><span className="detail-label">Week:</span><span className="detail-value">{pendingMilitarySession.week_number} of 6</span></div>
              <div className="detail-row"><span className="detail-label">Progress:</span><span className="detail-value">{pendingMilitarySession.answered_count} / {pendingMilitarySession.total_questions} questions</span></div>
              <div className="progress-bar-container" style={{ marginTop: 12 }}>
                <div className="progress-bar-fill" style={{ width: `${(pendingMilitarySession.answered_count / pendingMilitarySession.total_questions) * 100}%`, background: 'linear-gradient(90deg,#27ae60,#2ecc71)' }} />
              </div>
            </div>
            <p>Would you like to continue your assessment?</p>
            <div className="confirm-actions">
              <button className="decline-btn" onClick={() => { setShowMilitarySessionConfirm(false); setPendingMilitarySession(null); }}>Later</button>
              <button className="accept-btn" onClick={handleJoinMilitarySessionWrapper} style={{ background: '#27ae60' }}>Continue Assessment</button>
            </div>
          </div>
        </div>
      )}

      {showGroupVideoCall && groupInterviewId && (
        <div className={`video-call-modal-overlay ${isGroupCallMinimized ? 'minimized-overlay' : ''}`}>
          <div className={`video-call-modal-container ${isGroupCallMinimized ? 'minimized-container' : ''}`}>
            <InterviewVideoCall
              roomId={String(groupInterviewId)}
              userId={currentUserId}
              token={token}
              interviewId={groupInterviewId}
              user={user}
              isAdmin={isAdmin}
              role={isAdmin ? 'admin' : 'candidate'}
              candidateId={currentInterviewForCandidate?.candidateId || null}
              questions={groupQuestions}
              participants={groupCandidates}
              teamName={selectedTeam?.team_name || currentInterviewForCandidate?.teamName}
              isMinimized={isGroupCallMinimized}
              onToggleMinimize={() => setIsGroupCallMinimized(prev => !prev)}
              onMeetingEnd={handleMeetingEndedCleanup}
              teamId={selectedTeam?.team_id}
            />
          </div>
        </div>
      )}

      {showMilitaryVideoCall && militarySessionId && !showBatchManager && (
        <div className={`military_video-call-modal-overlay ${isMilitaryCallMinimized ? 'minimized-overlay' : ''}`}>
          <div className="military_video-call-container">
            <MilitarySessionVideoCall
              sessionId={militarySessionId}
              programId={activeMilitarySession?.program_id}
              weekNumber={activeMilitarySession?.week_number}
              userId={currentUserId}
              token={token}
              user={user}
              selectedUser={videoCallSelectedUser}
              isAdmin={isAdmin}
              teamName={selectedTeam?.team_name || pendingMilitarySession?.team_name}
              onSessionComplete={handleMilitarySessionComplete}
              onClose={handleMilitarySessionClose}
              onAbandonSession={abandonSession}
              isMinimized={isMilitaryCallMinimized}
              onToggleMinimize={() => setIsMilitaryCallMinimized(prev => !prev)}
            />
          </div>
        </div>
      )}

      {showBatchManager && currentBatchId && (
        <MilitaryBatchManager
          batchId={currentBatchId}
          token={token}
          teamId={selectedTeam?.team_id}
          teamName={selectedTeam?.team_name}
          user={user}
          onClose={handleBatchManagerClose}
          onBatchComplete={handleBatchComplete}
        />
      )}

      {showAnalyticsModal && analyticsInterviewId && isAdmin && (
        <DetailedAnalyticsModal
          interviewId={analyticsInterviewId}
          token={token}
          candidateId={analyticsCandidateId}
          onClose={() => { setShowAnalyticsModal(false); setAnalyticsInterviewId(null); setAnalyticsCandidateId(null); }}
        />
      )}

      {showEndInterviewModal && isAdmin && (
        <EndInterviewModal
          show={showEndInterviewModal}
          onClose={() => setShowEndInterviewModal(false)}
          interviewId={endInterviewId || selectedTeam?.active_interview?.interview_id || activeInterviewIdRef.current}
          teamId={selectedTeam?.team_id}
          token={token}
          onAllReviewed={() => setShowEndInterviewModal(false)}
          onCandidateAction={handleCandidateAction}
        />
      )}

      {showPendingCandidates && (
        <PendingCandidatesModal
          show={showPendingCandidates}
          onClose={() => setShowPendingCandidates(false)}
          candidates={pendingCandidates}
          onStartInterview={handleStartInterviewWithPending}
          loading={loadingPendingCandidates}
        />
      )}
    </div>
  );
};

export default Teams;





// import React, { useState, useEffect, useRef, useCallback } from 'react';
// import { useSelector } from 'react-redux';
// import { io } from 'socket.io-client';

// import TeamsList from './components/TeamsList';
// import TeamFormModal from './components/TeamFormModal';
// import MemberCard from './components/MemberCard';
// import MemberDetailsModal from './components/MemberDetailsModal';
// import InterviewSetupModal from '../../components/Teams/interview/InterviewSetupModal';
// import AddMemberOptionsModal from './components/AddMemberOptionsModal';
// import InterviewVideoCall from '../../components/Teams/interview/InterviewVideoCall';
// import MilitarySessionVideoCall from '../../components/Teams/assessment/MilitarySessionVideoCall';
// import MilitaryBatchManager from '../../components/Teams/assessment/MilitaryBatchManager';
// import DetailedAnalyticsModal from '../../components/Teams/interview/DetailedAnalyticsModal';
// import AdminQuestionsView from '../../components/Teams/interview/AdminQuestionsView';
// import EndInterviewModal from '../../components/Teams/interview/EndInterviewModal';
// import PendingCandidatesModal from '../../components/Teams/interview/PendingCandidatesModal';

// import { getTeamTypeDisplay } from '../../utils/teamTypeUtils';
// import { useInterviewHandlers } from './hooks/useInterviewHandlers';
// import { useMilitarySessionHandlers } from './hooks/useMilitarySessionHandlers';
// import { useTeamOperations } from './hooks/useTeamOperations';
// import useEndInterview from './hooks/useEndInterview';

// import axios from 'axios';
// import config from '../../config';
// import './Teams.css';
// import { toast } from '../../components/Common/Toast';
// import { FaUserClock, FaUserPlus  } from 'react-icons/fa';

// const Teams = () => {
//   const { userInfo: user, token } = useSelector(state => state.user);
//   const currentUserId = user?.id || user?.user_id;

//   const [teams, setTeams] = useState([]);
//   const [selectedTeam, setSelectedTeam] = useState(null);
//   const [teamMembers, setTeamMembers] = useState([]);
//   const [friends, setFriends] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [isAdmin, setIsAdmin] = useState(false);
//   const [availableQuestions, setAvailableQuestions] = useState([]);

//   const [isCheckingInterview, setIsCheckingInterview] = useState(false);
//   const [hasCheckedInitialInterview, setHasCheckedInitialInterview] = useState(false);
//   const [isStartingInterview, setIsStartingInterview] = useState(false);
//   const [activeInterview, setActiveInterview] = useState(null);
//   const [interviewQuestions, setInterviewQuestions] = useState([]);
//   const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
//   const [currentQuestion, setCurrentQuestion] = useState(null);
//   const [isInterviewActive, setIsInterviewActive] = useState(false);
//   const [interviewStartTime, setInterviewStartTime] = useState(null);
//   const [showCandidateQuestions, setShowCandidateQuestions] = useState(false);
//   const [currentInterviewForCandidate, setCurrentInterviewForCandidate] = useState(null);
//   const [pendingInterview, setPendingInterview] = useState(null);
//   const [showInterviewConfirm, setShowInterviewConfirm] = useState(false);
//   const [waitingForNext, setWaitingForNext] = useState(false);
//   const [nextQuestionTime, setNextQuestionTime] = useState(null);
//   const [interviewCompleted, setInterviewCompleted] = useState(false);
//   const [answeredQuestions, setAnsweredQuestions] = useState([]);
//   const [candidateProgress, setCandidateProgress] = useState(null);
//   const [askedQuestionIds, setAskedQuestionIds] = useState([]);
//   const [currentRound, setCurrentRound] = useState(null);
//   const [completedRounds, setCompletedRounds] = useState([]);
//   const [totalRounds] = useState(5);

//   const [showGroupVideoCall, setShowGroupVideoCall] = useState(false);
//   const [groupInterviewId, setGroupInterviewId] = useState(null);
//   const [groupQuestions, setGroupQuestions] = useState([]);
//   const [groupCandidates, setGroupCandidates] = useState([]);
//   const [isGroupCallMinimized, setIsGroupCallMinimized] = useState(false);

//   const [videoCallSelectedUser, setVideoCallSelectedUser] = useState(null);

//   const [showMilitaryVideoCall, setShowMilitaryVideoCall] = useState(false);
//   const [militarySessionId, setMilitarySessionId] = useState(null);
//   const [activeMilitarySession, setActiveMilitarySession] = useState(null);
//   const [isMilitaryCallMinimized, setIsMilitaryCallMinimized] = useState(false);
//   const [pendingMilitarySession, setPendingMilitarySession] = useState(null);
//   const [showMilitarySessionConfirm, setShowMilitarySessionConfirm] = useState(false);
//   const [hasCheckedInitialMilitarySession, setHasCheckedInitialMilitarySession] = useState(false);

//   const [showBatchManager, setShowBatchManager] = useState(false);
//   const [currentBatchId, setCurrentBatchId] = useState(null);
//   const [currentBatchData, setCurrentBatchData] = useState(null);
//   const [activeBatchSessions, setActiveBatchSessions] = useState([]);

//   const [showTeamFormModal, setShowTeamFormModal] = useState(false);
//   const [showAddMemberOptions, setShowAddMemberOptions] = useState(false);
//   const [showMemberDetails, setShowMemberDetails] = useState(null);
//   const [teamToEdit, setTeamToEdit] = useState(null);
//   const [showInterviewModal, setShowInterviewModal] = useState(false);
//   const [formMode, setFormMode] = useState('create');
//   const [showAdminQuestions, setShowAdminQuestions] = useState(false);
//   const [selectedInterviewId, setSelectedInterviewId] = useState(null);

//   const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
//   const [analyticsInterviewId, setAnalyticsInterviewId] = useState(null);
//   const [analyticsCandidateId, setAnalyticsCandidateId] = useState(null);
//   const [memberSearchQuery, setMemberSearchQuery] = useState('');

//   const [showPendingCandidates, setShowPendingCandidates] = useState(false);
//   const [pendingCandidates, setPendingCandidates] = useState([]);
//   const [loadingPendingCandidates, setLoadingPendingCandidates] = useState(false);

//   const timers = useRef({});
//   const askedQuestionsRef = useRef([]);
//   const mountedRef = useRef(true);
//   const activeInterviewIdRef = useRef(null);
//   const isInterviewEndedByAdminRef = useRef(false);

//   let fetchTeamMembers = null;

//   const clearInterviewTimers = () => {
//     if (timers.current.first) clearTimeout(timers.current.first);
//     if (timers.current.interval) clearInterval(timers.current.interval);
//     if (timers.current.candidate) clearTimeout(timers.current.candidate);
//     if (timers.current.candidateInterval) clearInterval(timers.current.candidateInterval);
//   };

//   const handleStartMilitaryProgram = (programData) => {
//     console.log('Military program started:', programData);

//     if (programData.candidates && programData.candidates.length > 0) {
//       setCurrentBatchId(programData.batchId);
//       setCurrentBatchData(programData);
//       setShowBatchManager(true);
//       toast.success(`Started assessment for ${programData.candidates.length} candidate(s)`);
//       setShowAddMemberOptions(false);
//       checkActiveBatchSessions();
//     } else if (programData.session) {
//       setMilitarySessionId(programData.session.session_id);
//       setActiveMilitarySession(programData.session);
//       setVideoCallSelectedUser(programData.selectedFriend || {
//         first_name: programData.candidate_name || 'Candidate',
//         last_name: '',
//         user_id: programData.user_id,
//       });
//       setShowMilitaryVideoCall(true);
//       setIsMilitaryCallMinimized(false);
//       setShowAddMemberOptions(false);
//     }
//   };

//   const {
//     loadAvailableQuestions,
//     checkActiveInterview,
//     checkIfUserInActiveInterview,
//     checkPendingInterviewQuestions,
//     logCandidateQuestion,
//     askCandidateQuestion,
//     handleStartInterview,
//     handleEndInterview: endInterviewPerCandidate,
//     getNextQuestion,
//   } = useInterviewHandlers(
//     token, user, selectedTeam, activeInterview, setActiveInterview, setIsInterviewActive,
//     setInterviewQuestions, setCurrentQuestion, setCurrentQuestionIndex,
//     setInterviewStartTime, setCandidateProgress, setCurrentInterviewForCandidate,
//     setShowCandidateQuestions, setWaitingForNext, setNextQuestionTime, setInterviewCompleted,
//     setPendingInterview, setShowInterviewConfirm, setShowAddMemberOptions, setShowInterviewModal,
//     activeInterviewIdRef, isInterviewEndedByAdminRef, askedQuestionsRef,
//     timers, availableQuestions, setAvailableQuestions, isAdmin,
//     isCheckingInterview, setIsCheckingInterview, showCandidateQuestions, showInterviewConfirm,
//     waitingForNext, interviewCompleted, isInterviewActive, interviewQuestions, currentQuestionIndex,
//     currentQuestion, currentInterviewForCandidate, fetchTeamMembers, setIsStartingInterview,
//     setLoading, isStartingInterview, setAskedQuestionIds, mountedRef, setCurrentRound,
//     totalRounds, setCompletedRounds, completedRounds, setAnsweredQuestions, setTeams, setSelectedTeam
//   );

//   const {
//     checkUserActiveMilitarySession,
//     handleStartMilitarySession,
//     handleStartMilitaryFromAddModal,
//     handleJoinMilitarySession,
//     handleMilitarySessionComplete,
//     handleMilitarySessionClose,
//     abandonSession,
//   } = useMilitarySessionHandlers(
//     token, user, selectedTeam,
//     setShowMilitaryVideoCall, setMilitarySessionId, setActiveMilitarySession,
//     setIsMilitaryCallMinimized, setVideoCallSelectedUser,
//     setPendingMilitarySession, setShowMilitarySessionConfirm,
//     teamMembers, fetchTeamMembers, handleStartMilitaryProgram
//   );

//   const {
//     fetchTeams, fetchFriends,
//     fetchTeamMembers: fetchTeamMembersOp,
//     handleCreateTeam, handleUpdateTeam, handleDeleteTeam,
//     handleAddMember, handleRemoveMember, handleExitTeam,
//   } = useTeamOperations(
//     token, user, teams, setTeams, selectedTeam, setSelectedTeam,
//     teamMembers, setTeamMembers, setShowTeamFormModal, teamToEdit, setTeamToEdit,
//     setShowAddMemberOptions, setLoading, setFriends
//   );

//   fetchTeamMembers = fetchTeamMembersOp;

// const {
//   handleEndInterview,
//   showEndInterviewModal,
//   setShowEndInterviewModal,
//   handleCandidateAction,
//   handleMeetingEndedCleanup,
//   endInterviewId,
//   isEnding
// } = useEndInterview(
//   token,
//   selectedTeam,
//   fetchTeamMembers,
//   setActiveInterview,
//   setIsInterviewActive,
//   setTeams,
//   setSelectedTeam,
//   setShowGroupVideoCall,
//   setIsGroupCallMinimized,
//   activeInterviewIdRef,
//   clearInterviewTimers
// );

//   const handleStartGroupInterview = async (interviewConfig) => {
//     try {
//       const result = await handleStartInterview(interviewConfig);

//       if (result?.interview) {
//         setGroupInterviewId(result.interview.interview_id);
//         setGroupQuestions(result.interview.questions || []);
//         setGroupCandidates(result.interview.candidates || []);
//         setShowGroupVideoCall(true);
//         setIsGroupCallMinimized(false);
//       }
//     } catch (err) {
//       console.error('Failed to start group interview:', err);
//       toast.error('Failed to start interview');
//     }
//   };

//   const handleGroupVideoCallEnd = () => {
//   console.log('handleGroupVideoCallEnd called - cleaning up interview UI');
  
//   // Clear video call states
//   setShowGroupVideoCall(false);
//   setIsGroupCallMinimized(false);
//   setGroupInterviewId(null);
//   setGroupQuestions([]);
//   setGroupCandidates([]);
  
//   // Clear interview state
//   setActiveInterview(null);
//   setIsInterviewActive(false);
//   setCurrentInterviewForCandidate(null);
//   setShowCandidateQuestions(false);
//   setWaitingForNext(false);
//   setInterviewCompleted(false);
//   setPendingInterview(null);
//   setShowInterviewConfirm(false);
  
//   // Clear refs
//   activeInterviewIdRef.current = null;
//   isInterviewEndedByAdminRef.current = false;
  
//   // Clear timers
//   clearInterviewTimers();
  
//   // Refresh team data
//   if (selectedTeam?.team_id && fetchTeamMembers) {
//     fetchTeamMembers(selectedTeam.team_id);
//   }
  
//   // Refresh teams list
//   fetchTeams();
// };

//   const handleAcceptInterview = (interview) => {
//     activeInterviewIdRef.current = interview.interview_id;

//     setShowInterviewConfirm(false);

//     setCurrentInterviewForCandidate({
//       interviewId: interview.interview_id,
//       teamName: interview.team_name,
//       role: interview.role || 'candidate',
//       candidateId: interview.candidateId,
//       questions: interview.questions || [],
//     });

//     setShowCandidateQuestions(false);
//     setWaitingForNext(false);
//     setInterviewCompleted(false);
//     setPendingInterview(null);
//     isInterviewEndedByAdminRef.current = false;

//     setGroupInterviewId(interview.interview_id);
//     setGroupQuestions(interview.questions || []);
//     setGroupCandidates(interview.candidates || []);
//     setShowGroupVideoCall(true);
//     setIsGroupCallMinimized(false);
//   };

//   const handleSelectTeam = (team) => {
//     setActiveInterview(null);
//     setIsInterviewActive(false);
//     setInterviewQuestions([]);
//     setCurrentQuestionIndex(0);
//     setCurrentQuestion(null);
//     setInterviewStartTime(null);
//     setCandidateProgress(null);

//     setShowMilitaryVideoCall(false);
//     setMilitarySessionId(null);
//     setActiveMilitarySession(null);

//     setShowBatchManager(false);
//     setCurrentBatchId(null);
//     setCurrentBatchData(null);

//     setShowGroupVideoCall(false);
//     setGroupInterviewId(null);
//     setGroupCandidates([]);
//     setGroupQuestions([]);

//     setVideoCallSelectedUser(null);
//     isInterviewEndedByAdminRef.current = false;
//     activeInterviewIdRef.current = null;

//     clearInterviewTimers();

//     setSelectedTeam(team);
//     setIsAdmin(String(team.created_by) === String(currentUserId));
//     fetchTeamMembersOp(team.team_id);
//   };

//   const handleJoinMilitarySessionWrapper = () => {
//     handleJoinMilitarySession(pendingMilitarySession);
//   };

//   const handleBatchManagerClose = () => {
//     setShowBatchManager(false);
//     setCurrentBatchId(null);
//     setCurrentBatchData(null);

//     if (selectedTeam?.team_id) {
//       fetchTeamMembersOp(selectedTeam.team_id);
//     }

//     checkActiveBatchSessions();
//   };

//   const handleBatchComplete = () => {
//     if (selectedTeam?.team_id) {
//       fetchTeamMembersOp(selectedTeam.team_id);
//     }

//     toast.success('Batch assessment session completed');
//     checkActiveBatchSessions();
//   };

//   const checkActiveBatchSessions = useCallback(async () => {
//     if (!selectedTeam?.team_id || !currentUserId) return;

//     try {
//       const response = await axios.get(
//         `${config.baseURL}/api/military/teams/${selectedTeam.team_id}/active-batches`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       if (response.data.success && response.data.batches) {
//         setActiveBatchSessions(response.data.batches);
//       }
//     } catch (error) {
//       console.error('Error checking active batches:', error);
//     }
//   }, [selectedTeam?.team_id, token, currentUserId]);

//   const handleOpenBatchManager = (batchId) => {
//     setCurrentBatchId(batchId);
//     setShowBatchManager(true);
//   };

//   useEffect(() => {
//     const handleOpenAnalytics = ({ detail }) => {
//       setAnalyticsInterviewId(detail.interviewId);
//       setAnalyticsCandidateId(detail.candidateId || null);
//       setShowAnalyticsModal(true);
//     };

//     window.addEventListener('openAnalyticsModal', handleOpenAnalytics);

//     return () => {
//       window.removeEventListener('openAnalyticsModal', handleOpenAnalytics);
//     };
//   }, []);

//   // Real-time interview invitation socket
//   useEffect(() => {
//     if (!token || !currentUserId) return;

//     const socket = io(config.baseURL, {
//       auth: { token },
//       // transports: ['websocket'],
//     });

//     socket.on("connect_error", (err) => {
//     console.log("Socket error:", err.message);
//   });

//     socket.on('connect', () => {
//       // console.log('Teams notification socket connected:', socket.id);
//     });
    

//     socket.on('interview-invitation', (interview) => {
//       // console.log('Interview invitation received:', interview);

//       // Check if already in an interview
//       if (activeInterviewIdRef.current || isInterviewActive || showGroupVideoCall) {
//         // console.log('Already in an interview, ignoring invitation');
//         return;
//       }

//       if (!interview?.interview_id) return;

//       setPendingInterview(interview);
//       setShowInterviewConfirm(true);
//     });

//     socket.on('interview-ended', (data) => {
//       console.log('Interview ended event received in Teams:', data);
      
//       const { interviewId, decision } = data;
      
//       // Check if this is our current interview
//       const isCurrentInterview = 
//         String(groupInterviewId) === String(interviewId) ||
//         String(activeInterviewIdRef.current) === String(interviewId);
      
//       if (isCurrentInterview) {
//         // Clear all interview-related states immediately
//         setShowGroupVideoCall(false);
//         setIsGroupCallMinimized(false);
//         setGroupInterviewId(null);
//         setGroupQuestions([]);
//         setGroupCandidates([]);
//         setActiveInterview(null);
//         setIsInterviewActive(false);
//         setCurrentInterviewForCandidate(null);
//         setShowCandidateQuestions(false);
//         setWaitingForNext(false);
//         setInterviewCompleted(false);
//         setPendingInterview(null);
//         setShowInterviewConfirm(false);
        
//         // Clear refs
//         activeInterviewIdRef.current = null;
//         isInterviewEndedByAdminRef.current = false;
        
//         // Clear timers
//         clearInterviewTimers();
        
//         // Refresh team data
//         if (selectedTeam?.team_id && fetchTeamMembers) {
//           fetchTeamMembers(selectedTeam.team_id);
//         }
        
//         // Refresh teams list
//         fetchTeams();
        
//         // Show appropriate message
//         if (decision === 'selected') {
//           toast.success('Interview completed - You have been selected!');
//         } else {
//           toast.info('Interview has ended');
//         }
//       }
//     });
    
//     socket.on('candidate-interview-ended', ({ interviewId, candidateId, decision, teamMemberAdded }) => {
//       // console.log('Candidate interview ended:', interviewId, candidateId, decision);
      
//       if (teamMemberAdded) {
//         toast.success('Candidate has been added to the team!');
//         // Refresh team members
//         if (selectedTeam?.team_id) {
//           fetchTeamMembers(selectedTeam.team_id);
//         }
//       }
//     });

//     return () => {
//       socket.off('connect');
//       socket.off('interview-invitation');
//       socket.off('interview-ended');
//       socket.off('candidate-interview-ended');
//       socket.disconnect();
//     };
//   }, [token]);
//   // }, [token, currentUserId, groupInterviewId, selectedTeam, activeInterviewIdRef, 
//   //     isInterviewActive, showGroupVideoCall, fetchTeamMembers]);

//   useEffect(() => {
//     if (currentUserId) {
//       fetchTeams();
//       fetchFriends();
//       loadAvailableQuestions();
//     }
//   }, [currentUserId]);

//   // Check active interview for selected team
//   useEffect(() => {
//     const checkActiveInterviewStatus = async () => {
//       if (!selectedTeam?.team_id) return;
      
//       try {
//         const { data } = await axios.get(
//           `${config.baseURL}/api/teams/${selectedTeam.team_id}/active-interview`,
//           { headers: { Authorization: `Bearer ${token}` } }
//         );
        
//         if (data?.success) {
//           if (data.active_interview) {
//             setActiveInterview(data.active_interview);
//             setIsInterviewActive(true);
//             activeInterviewIdRef.current = data.active_interview.interview_id;
            
//             // Update selected team with active interview
//             setSelectedTeam(prev => prev ? {
//               ...prev,
//               active_interview: data.active_interview
//             } : prev);
//           } else {
//             setActiveInterview(null);
//             setIsInterviewActive(false);
//             activeInterviewIdRef.current = null;
            
//             // Clear active interview from selected team
//             setSelectedTeam(prev => prev ? {
//               ...prev,
//               active_interview: null
//             } : prev);
//           }
//         }
//       } catch (err) {
//         console.error('Error checking active interview:', err);
//       }
//     };
    
//     if (selectedTeam && token) {
//       checkActiveInterviewStatus();
//     }
//   }, [selectedTeam?.team_id, token, setActiveInterview, setIsInterviewActive, setSelectedTeam]);

//   useEffect(() => {
//     if (selectedTeam) {
//       setIsAdmin(String(selectedTeam.created_by) === String(currentUserId));
//     }
//   }, [selectedTeam, currentUserId]);

//   useEffect(() => {
//     if (!currentUserId || !token || hasCheckedInitialInterview) return;

//     const runInitialChecks = async () => {
//       setHasCheckedInitialInterview(true);

//       let hasRegularInterview = false;

//       try {
//         const { data } = await axios.get(
//           `${config.baseURL}/api/teams/user/active-interview`,
//           { headers: { Authorization: `Bearer ${token}` } }
//         );

//         if (
//           data?.success &&
//           data?.active_interview &&
//           !showCandidateQuestions &&
//           !showInterviewConfirm &&
//           !waitingForNext &&
//           !interviewCompleted &&
//           !currentInterviewForCandidate
//         ) {
//           hasRegularInterview = true;
//           setPendingInterview(data.active_interview);
//           setShowInterviewConfirm(true);
//           setIsAdmin(String(data?.active_interview?.admin_id) === String(currentUserId))
//         }
//       } catch (err) {
//         console.error('Error checking active interview on mount:', err);
//       }

//       await checkUserActiveMilitarySession({
//         skipIfInterviewPending: hasRegularInterview,
//       });
//     };

//     runInitialChecks();
//   }, [currentUserId, token, hasCheckedInitialInterview]);

//   useEffect(() => {
//     mountedRef.current = true;

//     return () => {
//       mountedRef.current = false;
//       clearInterviewTimers();
//     };
//   }, []);

//   useEffect(() => {
//     const handleRefreshTeamMembers = async (event) => {
//       const { teamId } = event.detail;

//       if (selectedTeam?.team_id === teamId && fetchTeamMembersOp) {
//         await fetchTeamMembersOp(teamId);
//       }
//     };

//     window.addEventListener('refreshTeamMembers', handleRefreshTeamMembers);

//     return () => {
//       window.removeEventListener('refreshTeamMembers', handleRefreshTeamMembers);
//     };
//   }, [selectedTeam?.team_id, fetchTeamMembersOp]);

//   useEffect(() => {
//     if (selectedTeam) {
//       checkActiveBatchSessions();
//     }
//   }, [selectedTeam, checkActiveBatchSessions]);

//   const availableFriends = friends.filter(
//     f => !teamMembers.some(m => String(m.user_id) === String(f.user_id))
//   );

//   const filteredTeamMembers = teamMembers.filter(member => {
//     if (!memberSearchQuery.trim()) return true;
    
//     const query = memberSearchQuery.toLowerCase().trim();
//     const firstName = (member.first_name || '').toLowerCase();
//     const lastName = (member.last_name || '').toLowerCase();
//     const fullName = `${firstName} ${lastName}`.trim();
//     const email = (member.email || '').toLowerCase();
//     const username = (member.username || '').toLowerCase();
//     const profileId = (member.profileId || '').toLowerCase();
    
//     return (
//       firstName.includes(query) ||
//       lastName.includes(query) ||
//       fullName.includes(query) ||
//       email.includes(query) ||
//       profileId.includes(query) ||
//       username.includes(query) 
//     );
//   });

//   const getSelectedTeamTypeDisplay = () => {
//     return selectedTeam && getTeamTypeDisplay(selectedTeam);
//   };

//   const fetchPendingCandidates = useCallback(async () => {
//   if (!selectedTeam?.team_id) return;
  
//   setLoadingPendingCandidates(true);
//   try {
//     const { data } = await axios.get(
//       `${config.baseURL}/api/teams/${selectedTeam.team_id}/pending-candidates`,
//       { headers: { Authorization: `Bearer ${token}` } }
//     );
    
//     if (data.success) {
//       setPendingCandidates(data.candidates || []);
//     }
//   } catch (error) {
//     console.error('Error fetching pending candidates:', error);
//     toast.error('Failed to fetch pending candidates');
//   } finally {
//     setLoadingPendingCandidates(false);
//   }
// }, [selectedTeam?.team_id, token]);

// const handleStartInterviewWithPending = useCallback((candidate) => {
//   setShowPendingCandidates(false);
  
//   // Set up interview configuration for this candidate
//   const interviewConfig = {
//     mode: candidate.interview_mode || 'group',
//     capture: [],
//     candidates: [candidate.candidate_id]
//   };
  
//   handleStartGroupInterview(interviewConfig);
// }, [handleStartGroupInterview]);

// const handleOpenPendingCandidates = () => {
//   fetchPendingCandidates();
//   setShowPendingCandidates(true);
// };

//   if (!user) {
//     return (
//       <div className="teams-loading">
//         <div className="loading-spinner" />
//       </div>
//     );
//   }

//   return (
//     <div className="teams-page">
//       <div className="teams-header">
//         <h1 className="teams-title">Teams Management</h1>
//         <p className="teams-subtitle">
//           Create teams and manage members from your friends list
//         </p>
//       </div>

//       <div className="teams-layout">
//         <div className="teams-left-panel">
//           <TeamsList
//             teams={teams}
//             selectedTeam={selectedTeam}
//             onSelectTeam={handleSelectTeam}
//             onEditTeam={(team) => {
//               setTeamToEdit(team);
//               setFormMode('edit');
//               setShowTeamFormModal(true);
//             }}
//             onDeleteTeam={handleDeleteTeam}
//             onCreateTeam={() => {
//               setTeamToEdit(null);
//               setFormMode('create');
//               setShowTeamFormModal(true);
//             }}
//             currentUserId={currentUserId}
//             onExitTeam={handleExitTeam}
//           />
//         </div>

//         <div className="teams-right-panel">
//           {selectedTeam ? (
//             <>
//               <div className="team-details-header">
//                 <div className="team-title-section">
//                   <div className="team-title-row">
//                     <h2 className="team-details-title">
//                       {selectedTeam.team_name}
//                     </h2>

//                     {getSelectedTeamTypeDisplay() && (
//                       <span className={`team-type-simple ${getSelectedTeamTypeDisplay().className}`}>
//                         {getSelectedTeamTypeDisplay().fullDisplay}
//                       </span>
//                     )}
//                   </div>

//                   {selectedTeam.description && (
//                     <p className="team-details-description">
//                       {selectedTeam.description}
//                     </p>
//                   )}
//                 </div>

//                 <div className="team-actions">
//                   {isAdmin && (
//                     <>
//                       <button
//                         className="add-member-btn"
//                         onClick={() => setShowAddMemberOptions(true)}
//                         disabled={isInterviewActive}
//                       >
//                         <FaUserPlus /> Add Members
//                       </button>

//                        {/* Pending Candidates Button */}
//                       <button
//                         className="pending-candidates-btn"
//                         onClick={handleOpenPendingCandidates}
//                       >
//                         <FaUserClock /> Pending Candidates
//                       </button>

//                       {/* Show Manage Interview button when there's an active interview */}
//                       {isInterviewActive && selectedTeam?.active_interview && (
//                         <button
//                           className="manage-interview-btn"
//                           onClick={() => {
//                             const interview = selectedTeam.active_interview;
                            
//                             // Set up the interview state to rejoin
//                             setGroupInterviewId(interview.interview_id);
//                             setActiveInterview(interview);
//                             setGroupQuestions([]);
//                             setGroupCandidates([]);
                            
//                             // If we have candidate progress data for the admin view
//                             if (interview.interview_id) {
//                               activeInterviewIdRef.current = interview.interview_id;
//                             }
                            
//                             // Show the group video call
//                             setShowGroupVideoCall(true);
//                             setIsGroupCallMinimized(false);
//                           }}
//                         >
//                           Manage Interview
//                         </button>
//                       )}

//                       {activeBatchSessions.length > 0 && (
//                         <div className="active-batch-dropdown">
//                           <button
//                             className="manage-batch-btn"
//                             onClick={() => {
//                               if (activeBatchSessions.length === 1) {
//                                 handleOpenBatchManager(activeBatchSessions[0].batch_id);
//                               } else {
//                                 setShowBatchManager(false);
//                               }
//                             }}
//                           >
//                             Manage Assessments
//                           </button>

//                           {activeBatchSessions.length > 1 && (
//                             <div className="batch-dropdown-content">
//                               {activeBatchSessions.map(batch => (
//                                 <button
//                                   key={batch.batch_id}
//                                   className="batch-option"
//                                   onClick={() => handleOpenBatchManager(batch.batch_id)}
//                                 >
//                                   <div className="batch-option-info">
//                                     <span className="batch-id-display">
//                                       Batch:{' '}
//                                       {batch.batch_id.length > 10
//                                         ? `${batch.batch_id.slice(0, 8)}...`
//                                         : batch.batch_id}
//                                     </span>

//                                     <span className="batch-candidate-count">
//                                       {batch.candidate_count} candidate
//                                       {batch.candidate_count !== 1 ? 's' : ''}
//                                     </span>
//                                   </div>

//                                   <span className={`batch-status ${batch.status}`}>
//                                     Week {batch.current_week}
//                                   </span>
//                                 </button>
//                               ))}
//                             </div>
//                           )}
//                         </div>
//                       )}

//                       {/* {isInterviewActive && (
//                         <button
//                           className="end-interview-btn"
//                           onClick={() => handleEndInterview()}
//                         >
//                           End Interview
//                         </button>
//                       )} */}
//                     </>
//                   )}
//                 </div>
//               </div>

//               <div className="team-members-section">
//                 <div className="members-header">
//                   <h3 className="members-title">
//                     Team Members ({teamMembers.length})
//                   </h3>
                  
//                   {/* Search Bar */}
//                   {teamMembers.length > 0 && (
//                     <div className="member-search-container">
//                       <input
//                         type="text"
//                         className="member-search-input"
//                         placeholder="Name, ID and Email"
//                         value={memberSearchQuery}
//                         onChange={(e) => setMemberSearchQuery(e.target.value)}
//                       />
//                       {memberSearchQuery && (
//                         <button
//                           className="member-search-clear"
//                           onClick={() => setMemberSearchQuery('')}
//                           type="button"
//                         >
//                           ✕
//                         </button>
//                       )}
//                     </div>
//                   )}
//                 </div>

//                 {teamMembers.length === 0 ? (
//                   <div className="empty-members">
//                     <div className="empty-icon">👤</div>
//                     <h3>No Members Yet</h3>
//                     <p>Add members from your friends list</p>
//                   </div>
//                 ) : filteredTeamMembers.length === 0 ? (
//                   <div className="empty-members">
//                     <div className="empty-icon">🔍</div>
//                     <h3>No Members Found</h3>
//                     <p>No members match "{memberSearchQuery}"</p>
//                   </div>
//                 ) : (
//                   <div className="members-grid">
//                     {filteredTeamMembers.map(member => (
//                       <MemberCard
//                         key={member.team_member_id}
//                         isAdmin={isAdmin}
//                         user={user}
//                         token={token}
//                         member={member}
//                         onRemove={handleRemoveMember}
//                         onViewDetails={setShowMemberDetails}
//                         currentUserId={currentUserId}
//                         teamId={selectedTeam?.team_id}
//                         onStartInterview={handleStartGroupInterview}
//                         onStartMilitarySession={handleStartMilitarySession}
//                         teamName={selectedTeam?.team_name}
//                         teamType={selectedTeam?.team_type}
//                         onEndInterview={handleEndInterview}
//                       />
//                     ))}
//                   </div>
//                 )}
//               </div>
//             </>
//           ) : (
//             <div className="no-team-selected">
//               <div className="empty-state">
//                 <div className="empty-icon">🏢</div>
//                 <h3>Select a Team</h3>
//                 <p>Choose a team from the list to view members and details</p>

//                 <button
//                   className="create-team-btn-large"
//                   onClick={() => {
//                     setTeamToEdit(null);
//                     setFormMode('create');
//                     setShowTeamFormModal(true);
//                   }}
//                 >
//                   Create your team
//                 </button>
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//       <TeamFormModal
//         show={showTeamFormModal}
//         onClose={() => {
//           setShowTeamFormModal(false);
//           setTeamToEdit(null);
//         }}
//         onSubmit={formMode === 'edit' ? handleUpdateTeam : handleCreateTeam}
//         loading={loading}
//         teamToEdit={teamToEdit}
//         mode={formMode}
//       />

//       <InterviewSetupModal
//         show={showInterviewModal}
//         onClose={() => setShowInterviewModal(false)}
//         onStart={handleStartGroupInterview}
//         teamName={selectedTeam?.team_name}
//         selectedFriends={[]}
//       />

//       <AddMemberOptionsModal
//         show={showAddMemberOptions}
//         onClose={() => setShowAddMemberOptions(false)}
//         teamId={selectedTeam?.team_id}
//         teamName={selectedTeam?.team_name}
//         teamType={selectedTeam?.team_type}
//         friends={availableFriends}
//         teamMembers={teamMembers}
//         onAddMember={handleAddMember}
//         onStartInterview={handleStartGroupInterview}
//         onStartMilitaryProgram={handleStartMilitaryFromAddModal}
//         token={token}
//         user={user}
//       />

//       {showMemberDetails && (
//         <MemberDetailsModal
//           member={showMemberDetails}
//           onClose={() => setShowMemberDetails(null)}
//           currentUserId={currentUserId}
//         />
//       )}

//       {showInterviewConfirm && pendingInterview && !isAdmin && (
//         <div className="interview-confirm-overlay">
//           <div className="interview-confirm-modal">
//             <h2>Interview Invitation</h2>

//             <p>
//               You have been invited for an interview with team{' '}
//               <strong>{pendingInterview.team_name}</strong>
//             </p>

//             <p>
//               Total Questions:{' '}
//               {pendingInterview.questions?.length ||
//                 pendingInterview.total_questions ||
//                 25}
//             </p>

//             <p>Would you like to start now?</p>

//             <div className="confirm-actions">
//               <button
//                 className="decline-btn"
//                 onClick={() => {
//                   setShowInterviewConfirm(false);
//                   setPendingInterview(null);
//                 }}
//               >
//                 Later
//               </button>

//               <button
//                 className="accept-btn"
//                 onClick={() => handleAcceptInterview(pendingInterview)}
//               >
//                 Start Now
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {showMilitarySessionConfirm && pendingMilitarySession && (
//         <div className="interview-confirm-overlay">
//           <div className="interview-confirm-modal">
//             <h2>Military Assessment</h2>

//             <p>
//               You have an active military assessment with team{' '}
//               <strong>{pendingMilitarySession.team_name}</strong>
//             </p>

//             <div className="military-session-details">
//               <div className="detail-row">
//                 <span className="detail-label">Week:</span>
//                 <span className="detail-value">
//                   {pendingMilitarySession.week_number} of 6
//                 </span>
//               </div>

//               <div className="detail-row">
//                 <span className="detail-label">Progress:</span>
//                 <span className="detail-value">
//                   {pendingMilitarySession.answered_count} /{' '}
//                   {pendingMilitarySession.total_questions} questions
//                 </span>
//               </div>

//               <div className="progress-bar-container" style={{ marginTop: 12 }}>
//                 <div
//                   className="progress-bar-fill"
//                   style={{
//                     width: `${(pendingMilitarySession.answered_count / pendingMilitarySession.total_questions) * 100}%`,
//                     background: 'linear-gradient(90deg,#27ae60,#2ecc71)',
//                   }}
//                 />
//               </div>
//             </div>

//             <p>Would you like to continue your assessment?</p>

//             <div className="confirm-actions">
//               <button
//                 className="decline-btn"
//                 onClick={() => {
//                   setShowMilitarySessionConfirm(false);
//                   setPendingMilitarySession(null);
//                 }}
//               >
//                 Later
//               </button>

//               <button
//                 className="accept-btn"
//                 onClick={handleJoinMilitarySessionWrapper}
//                 style={{ background: '#27ae60' }}
//               >
//                 Continue Assessment
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
      
//       {showGroupVideoCall && groupInterviewId && (
//         <div className={`video-call-modal-overlay ${isGroupCallMinimized ? 'minimized-overlay' : ''}`}>
//           <div className={`video-call-modal-container ${isGroupCallMinimized ? 'minimized-container' : ''}`}>
//             <InterviewVideoCall
//               roomId={String(groupInterviewId)}
//               userId={currentUserId}
//               token={token}
//               interviewId={groupInterviewId}
//               user={user}
//               isAdmin={isAdmin}
//               role={isAdmin ? 'admin' : 'candidate'}
//               candidateId={currentInterviewForCandidate?.candidateId || null}
//               questions={groupQuestions}
//               participants={groupCandidates}
//               teamName={selectedTeam?.team_name || currentInterviewForCandidate?.teamName}
//               isMinimized={isGroupCallMinimized}
//               onToggleMinimize={() => setIsGroupCallMinimized(prev => !prev)}
//               onMeetingEnd={handleMeetingEndedCleanup}
//               teamId={selectedTeam?.team_id}
//             />
//           </div>
//         </div>
//       )}

//       {showMilitaryVideoCall && militarySessionId && !showBatchManager && (
//         <div className={`military_video-call-modal-overlay ${isMilitaryCallMinimized ? 'minimized-overlay' : ''}`}>
//           <div className="military_video-call-container">
//             <MilitarySessionVideoCall
//               sessionId={militarySessionId}
//               programId={activeMilitarySession?.program_id}
//               weekNumber={activeMilitarySession?.week_number}
//               userId={currentUserId}
//               token={token}
//               user={user}
//               selectedUser={videoCallSelectedUser}
//               isAdmin={isAdmin}
//               teamName={selectedTeam?.team_name || pendingMilitarySession?.team_name}
//               onSessionComplete={handleMilitarySessionComplete}
//               onClose={handleMilitarySessionClose}
//               onAbandonSession={abandonSession}
//               isMinimized={isMilitaryCallMinimized}
//               onToggleMinimize={() => setIsMilitaryCallMinimized(prev => !prev)}
//             />
//           </div>
//         </div>
//       )}

//       {showBatchManager && currentBatchId && (
//         <MilitaryBatchManager
//           batchId={currentBatchId}
//           token={token}
//           teamId={selectedTeam?.team_id}
//           teamName={selectedTeam?.team_name}
//           user={user}
//           onClose={handleBatchManagerClose}
//           onBatchComplete={handleBatchComplete}
//         />
//       )}

//       {showAnalyticsModal && analyticsInterviewId && isAdmin && (
//         <DetailedAnalyticsModal
//           interviewId={analyticsInterviewId}
//           token={token}
//           candidateId={analyticsCandidateId}
//           onClose={() => {
//             setShowAnalyticsModal(false);
//             setAnalyticsInterviewId(null);
//             setAnalyticsCandidateId(null);
//           }}
//         />
//       )}

//       {showEndInterviewModal && isAdmin && (
//         <EndInterviewModal
//           show={showEndInterviewModal}
//           onClose={() => setShowEndInterviewModal(false)}
//           interviewId={endInterviewId || selectedTeam?.active_interview?.interview_id || activeInterviewIdRef.current}
//           teamId={selectedTeam?.team_id}
//           token={token}
//           onAllReviewed={() => setShowEndInterviewModal(false)}
//           onCandidateAction={handleCandidateAction}
//         />
//       )}

//       {showPendingCandidates && (
//         <PendingCandidatesModal
//           show={showPendingCandidates}
//           onClose={() => setShowPendingCandidates(false)}
//           candidates={pendingCandidates}
//           onStartInterview={handleStartInterviewWithPending}
//           loading={loadingPendingCandidates}
//         />
//       )}
//     </div>
//   );
// };

// export default Teams;